import type { SerializedError } from '@reduxjs/toolkit';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  QueryReturnValue,
} from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { Blob as NodeBlob } from 'buffer';
import request from 'superagent';

import type { AbstractServiceResponse, ServiceError } from '@models';
import { logout } from '@store/actions/action-auth';

import type { RootState } from './index';

export const RTKQueryTagTypes = [
  'Applications',
  'ApplicationJobs',
  'ApplicationVersions',
  'ApplicationVersionAssets',
  'ApplicationVersionContents',
  'ApplicationVersionPackagesSummary',
  'ApplicationVersionPolicy',
  'ApplicationVersionVulnerabilities',
  'ApplicationVersionVulnerabilityAnnotations',
  'ApplicationVersionVulnerabilityPackages',
  'ApplicationVersionVulnerabilitySummary',
  'ArtifactVulnerabilities',
  'EventTypes',
  'ImpactedAppVersions',
  'MadRobotRules',
  'MadRobotState',
  'Policies',
  'VulnerabilityAnnotations',
] as const;

const isUnknownServiceResponse = (
  response: unknown,
): response is AbstractServiceResponse<unknown> =>
  !!response && typeof response === 'object' && 'status' in response;

const unwrapServiceResponse = (response: AbstractServiceResponse<unknown>) =>
  ('body' in response && response.body.data) ||
  ('data' in response && response.data) ||
  response;

export const unwrapQueryErrorMessage = (
  error: FetchBaseQueryError | SerializedError,
): string =>
  'data' in error
    ? (error.data as { message: string }).message
    : (error as SerializedError).message || 'Something went wrong';

/**
 * Converts a ServiceResponse with status 'error' to an RTK Query error format.
 * This allows us to use the error property in components consistently across
 * all API endpoints, regardless of whether the error comes from HTTP status
 * codes or our custom service response format.
 */
const convertServiceResponseToRtkError = <T extends QueryReturnValue>(
  result: T,
) => {
  // Check if the response has our custom status field
  if (isUnknownServiceResponse(result.data)) {
    const response = result.data;

    // If status is 'error', transform it to an RTK Query error
    if (response.status === 'error') {
      return {
        error: {
          status: 'CUSTOM_ERROR',
          data: unwrapServiceResponse(response),
          error: 'Server returned error status',
        } satisfies FetchBaseQueryError,
      };
    }
  }

  return result;
};

// Custom base query using SuperAgent (consistent with existing Redux slices)
// TODO in the future, consider using fetch
const baseQueryWithErrorHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api) => {
  try {
    const state = api.getState() as RootState;

    // Determine URL and method
    const url = typeof args === 'string' ? args : args.url;
    const method = typeof args === 'string' ? 'GET' : args.method || 'GET';
    const body = typeof args !== 'string' ? args.body : undefined;
    const params = typeof args !== 'string' ? args.params : undefined;

    // Build the full URL with base path
    const fullUrl = url.startsWith('/service') ? url : `/service${url}`;

    let req = request[
      method.toLowerCase() as 'get' | 'post' | 'put' | 'delete' | 'patch'
    ](fullUrl).query(params || {});

    // Add authentication headers if user is authenticated
    if (state.auth.isAuthenticated && state.auth.account?.sessionID) {
      req = req.set('X-Session-ID', state.auth.account.sessionID);
    }

    // Add socket ID header if available
    if (typeof window !== 'undefined' && window.sock?.id) {
      req = req.set('anchore-socketid', window.sock.id);
    }

    // Add request body for mutation methods
    if (body && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      // When the body contains File or Blob values, use SuperAgent's multipart
      // API (.field / .attach) so the request is sent as multipart/form-data.
      // This avoids manually constructing FormData, which has cross-environment
      // compatibility issues between Node.js and JSDOM.
      //
      // NOTE: Only top-level values are inspected — Blobs nested inside child
      // objects won't be detected. Keep the body flat when mixing file and text
      // fields (serialize nested structures to JSON strings before sending).
      if (
        typeof body === 'object' &&
        !Array.isArray(body) &&
        Object.values(body as Record<string, unknown>).some(
          v => v instanceof Blob,
        )
      ) {
        for (const [key, value] of Object.entries(
          body as Record<string, unknown>,
        )) {
          if (value instanceof Blob) {
            req = req.attach(
              key,
              value as unknown as NodeBlob,
              (value as File).name,
            );
          } else if (value !== undefined && value !== null) {
            req = req.field(
              key,
              typeof value === 'string' ? value : JSON.stringify(value),
            );
          }
        }
      } else {
        req = req.send(body);
      }
    }

    // Execute request
    const response = await req;

    const result = { data: response.body };

    // Handle success responses possibly containing error objects
    return convertServiceResponseToRtkError(result);
  } catch (error: any) {
    // Extract error details from the response body if available
    const responseBody = error.response?.body;
    const unwrappedResponse: ServiceError | null = responseBody
      ? unwrapServiceResponse(responseBody)
      : null;

    // Fallback for non-structured errors
    const errorMessage =
      unwrappedResponse?.message ||
      error.message ||
      'Network request failed. Please try again.';

    const errorStatus =
      error.status || unwrappedResponse?.statusCode || 'CUSTOM_ERROR';

    // If this is a 401 error and the user is authenticated, trigger logout
    // This handles the case where session data is lost (e.g., Redis restart)
    // and any API call returns 401
    if (errorStatus === 401) {
      const state = api.getState() as RootState;
      if (state.auth.isAuthenticated) {
        // Dispatch logout action; server-side logout already happened (`401`
        // from server)
        api.dispatch(logout({ server: false }));
      }
    }

    return {
      error: {
        status: errorStatus,
        data: unwrappedResponse || errorMessage,
        error: errorMessage,
      } satisfies FetchBaseQueryError,
    };
  }
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: RTKQueryTagTypes,
  endpoints: () => ({}),
});

export type ApiEndpoints = typeof api.endpoints;
